<script setup>
import {computed, nextTick, onMounted, ref} from 'vue';
    import { useNotificationStore } from '@/stores/NotificationStore.js'
    const notificationStore = useNotificationStore()

    const emit = defineEmits(['close', 'doReloadLeads']);

    const props = defineProps({
        selectedLeads: {
            type: Array,
            required: true
        },
        showTopbar: {
            type: Boolean,
            required: false,
            default: true,
        }
    });

    const loadingTags = ref(false);
    const working = ref(false);
    const availableTags = ref([]);
    const search = ref('');
    const showAll = ref(false);
    const selectedTagId = ref(null);
    const tagSearchInputRef = ref(null);

    const loadTags = async () => {
        loadingTags.value = true;
        try {
            await axios.get(route('crm.leadtags.index')).then((response) => {
                availableTags.value = response.data.tags;
            });
            loadingTags.value = false;
            await nextTick();
            tagSearchInputRef.value.focusInput();
        } catch (error) {
            console.error('Error loading tags:', error);
        } finally {
            loadingTags.value = false;
        }
    }

    const selectTag = (tagId) => {
        if (selectedTagId.value === tagId) {
            selectedTagId.value = null;
        } else {
            selectedTagId.value = tagId;
        }
    }

    const detachTag = async () => {
        working.value = true;
        try {
            const response = await axios.post(
                route('crm.detachTags', selectedTagId.value),
                {
                    selected: props.selectedLeads,
                }
            );

            search.value = '';
            notificationStore.addNotification({
                type: 'success',
                title: response.data.tag.name,
                message: _mft('crm:tag.removed'),
                dismissAfter: 2500,
            });

            emit('doReloadLeads');
            selectedTagId.value = null;
        } catch (error) {
            notificationStore.addNotification({
                type: 'error',
                message: _mft('error:error.generalOccurred'),
                dismissAfter: 2500,
            });
        } finally {
            working.value = false;
        }
    }

    const filteredTags = computed(() => {
        return availableTags.value.filter((tag) => {
            return ! tag.is_system_tag;
        }).filter((tag) => {
            return tag.name.toLowerCase().includes(search.value.toLowerCase());
        });
    });

    const limitedFilteredTags = computed(() => {
        if (showAll.value === true) {
            return filteredTags.value;
        } else {
            return filteredTags.value.slice(0, 5);
        }
    });

    const moreTagsAvailable = computed(() => {
        return filteredTags.value.length - 5;
    });

    const close = () => {
        emit('close');
    }

    onMounted(() => {
        loadTags();
    });

</script>
<template>
    <div>
        <div
            v-if="props.showTopbar"
            class="flex justify-between space-x-2">
            <div class="text-gray-400">
                {{ _mft('crm:tag.leadsSelected', {count: selectedLeads.length }) }}
            </div>
            <button-danger
                class="text-white bg-red-700 !border-0"
                size="sm"
                @click="close"
            >
                {{ _mft('shared:cancel') }}
            </button-danger>
        </div>
        <div class="relative mb-2">
            <input-text
                class="!mb-0"
                v-model:content="search"
                :maxlength="24"
                ref="tagSearchInputRef"
                :placeholder="_mft('crm:tag.searchTagsToRemove')"
                :disabled="loadingTags"
            />
        </div>
        <div v-if="loadingTags" class="flex justify-center py-1">
            <mf-spinner-medium class="w-12 h-6"></mf-spinner-medium>
        </div>
        <div
            v-if="! loadingTags"
            class="relative"
            :class="working ? 'opacity-20' : ''"
        >
            <div
                v-if="working"
                class="absolute inset-0 flex justify-center items-center"
            >
                <mf-spinner-medium class="w-12 h-6"></mf-spinner-medium>
            </div>
            <ul class="mb-4 flex flex-wrap">
                <li
                    v-for="tag in limitedFilteredTags"
                    :key="tag.id"
                    @click="selectTag(tag.id)"
                    class="mr-2 mb-1 cursor-pointer text-black px-1.5 py-0.5 border flex items-center border-gray-300 rounded-full"
                    :class="selectedTagId === tag.id ? 'bg-gray-300 ring-2 ring-gray-500' : 'bg-gray-200 hover:bg-gray-300'"
                    :style="tag.color ? {backgroundColor: tag.color, color: tag.textcolor} : {}"
                >
                    <component
                        :is="tag.description ? 'container-with-tooltip' : 'span'"
                        class="text-xs"
                    >
                        {{ tag.name }}
                        <template
                            v-if="tag.description"
                            #tooltip
                        >
                            <div class="block w-24 text-xxs text-black">
                                {{ tag.description }}
                            </div>
                        </template>
                    </component>
                </li>
                <li
                    v-if="moreTagsAvailable > 0 && !showAll"
                    class="mr-2 cursor-pointer mt-0.5 mf-link text-xs"
                    @click="showAll = true"
                >{{ _mft('crm:tag.andAnotherNMore', {count: moreTagsAvailable }) }}</li>
            </ul>
            <div class="flex justify-end space-x-4">
                <slot name="actions"></slot>
                <button-base
                    :style-type="!selectedTagId || selectedLeads.length === 0 ? 'disabled' : 'danger'"
                    @click="detachTag"
                    size="sm"
                    :disabled="!selectedTagId || selectedLeads.length === 0"
                >
                    <span v-if="selectedTagId && selectedLeads.length > 0">
                        {{ _mft('crm:tag.removeFromNLeads', {count: selectedLeads.length}) }}
                    </span>
                    <span v-else>{{ _mft('crm:tag.selectTagToRemove') }}</span>
                </button-base>
            </div>
        </div>
    </div>
</template>
